import {
  eqBy,
  filter,
  flatten,
  map,
  move,
  omit,
  pick,
  pipe,
  prop,
  unionWith,
} from 'ramda';

import { t } from '../../../../../../modules/infra/i18n';
import { REPORT_TYPES } from '../../constants';

const transformDataItem = (objectEntry, isBreakdown) => ({
  fieldName: objectEntry[0],
  fieldValue: objectEntry[1],
  isBreakdown,
});

const transformBreakdownObject = breakdownObject =>
  Object.entries(breakdownObject).map(entry => transformDataItem(entry, true));

const getNameField = dataObject => Object.entries(dataObject)[0][0];

const transformDataObject = (dataObject) => {
  const nameField = getNameField(dataObject);
  const name = dataObject[nameField];
  const sansName = omit([nameField], dataObject);
  const data = Object.entries(sansName).map(entry =>
    ((typeof entry[1] === 'object') ? transformBreakdownObject(entry[1]) : transformDataItem(entry, false))
  );

  return {
    name,
    data: flatten(data),
  };
};

export const transformFiltersToData = map(pipe(
    pick(['title']),
    item => ({ name: item.title, data: [] })
));

export const moveTotalsToTop = (data) => {
  let result = data;

  const totalsIndex = result.findIndex(item => item.name === 'Totals');
  if (result.length === 2 && totalsIndex >= 0) {
    result = filter(item => item.name !== 'Totals', result);
  } else if (result.length > 2) {
    if (totalsIndex > 0) {
      result = move(totalsIndex, 0, result);
    }
  }

  return result;
};

/**
 * @param {Object[]} dataFromApi - data received from api in the following format:
 * [
 *  {
 *    "Location": "Glassbox Hamilton",
 *    "Gross Sales": "US$3,481.67",
 *    "Net Service Sales Breakdown": {
 *      "// Beard Trim": "US$17.70",
 *      "Buzz Cut // Maintenance": "US$35.40",
 *    },
 *  },
 *  {
 *    "Name": "Totals",
 *    "Gross Sales": "US$7,521.39",
 *    "Net Service Sales Breakdown": {
 *      "// Beard Trim": "US$39.82",
 *      "Buzz Cut // Maintenance": "US$274.35",
 *    },
 *  }
 * ]
 *
 * @returns {Object[]} - data transformed to UI friendly format:
 * [
 *  {
 *    name: 'Totals',
 *    data: [
 *      {
 *        fieldName: 'Gross Sales',
 *        fieldValue: 'US$7,521.39',
 *      },
 *      {
 *        fieldName: '// Beard Trim',
 *        fieldValue: 'US$39.82',
 *        isBreakdown: true,
 *      },
 *      {
 *        fieldName: 'Buzz Cut // Maintenance',
 *        fieldValue: 'US$274.35',
 *        isBreakdown: true,
 *      },
 *    ],
 *  },
 *  {
 *    name: 'Glassbox Hamilton',
 *    data: [
 *      {
 *        fieldName: 'Gross Sales',
 *        fieldValue: 'US$3,481.67',
 *      },
 *      {
 *        fieldName: '// Beard Trim',
 *        fieldValue: 'US$17.70',
 *        isBreakdown: true,
 *      },
 *      {
 *        fieldName: 'Buzz Cut // Maintenance',
 *        fieldValue: 'US$35.40',
 *        isBreakdown: true,
 *      },
 *    ],
 *  },
 * ];
 */
export const transformReportData = (dataFromApi, filteredItems) => {
  const transformedData = dataFromApi.map(entry => transformDataObject(entry));
  const transformedFilters = transformFiltersToData(filteredItems);

  // api doesn't return any rows for items that have no data in star schema
  // so we add those items with empty data to the result from api
  const result = unionWith(eqBy(prop('name')), transformedData, transformedFilters);
  return moveTotalsToTop(result);
};


const getDataTabName = (tabData, reportDefinition) => {
  if (tabData.name === 'Totals') {
    return reportDefinition.type === REPORT_TYPES.BARBER
      ? t('reports:custom_report.total_selected_barbers')
      : t('reports:custom_report.total_selected_locations');
  }

  return tabData.name;
};

export const getDataTabNames = (reportData, reportDefinition) =>
  reportData.map(tabData => getDataTabName(tabData, reportDefinition));
